import React, { useContext, useEffect, useRef, useState } from 'react';
import { motion } from "framer-motion";
import { Mail, Linkedin, Github, MapPin, ArrowRight, ChevronDown } from "lucide-react";
import TelegramQRCard, { TelegramIcon } from '../components/ui/TelegramQRCard';
import createGlobe from 'cobe';
import { ThemeContext } from '../context/ThemeContext';

const CobeGlobe = ({ theme }) => {
  const canvasRef = useRef();
  const hasMarkedReadyRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let phi = 0;
    let width = 0;
    hasMarkedReadyRef.current = false;
    setIsReady(false);

    const onResize = () => canvasRef.current && (width = canvasRef.current.offsetWidth);
    window.addEventListener('resize', onResize);
    onResize();

    const globe = createGlobe(canvasRef.current, {
      devicePixelRatio: 2,
      width: width * 2 || 1000,
      height: width * 2 || 1000,
      phi: 0,
      theta: 0.2,
      dark: theme === 'dark' ? 1 : 0,
      diffuse: 1.2,
      mapSamples: 16000,
      mapBrightness: 6,
      baseColor: theme === 'dark' ? [0.15, 0.15, 0.15] : [0.9, 0.93, 1],
      markerColor: [0.14, 0.5, 1], // primary blue
      glowColor: theme === 'dark' ? [0.1, 0.1, 0.15] : [0.75, 0.85, 1],
      markers: [
        { location: [20.5937, 78.9629], size: 0.08 } // India
      ],
      onRender: (state) => {
        state.phi = phi;
        phi += 0.003;
        state.width = width * 2;
        state.height = width * 2;
        if (!hasMarkedReadyRef.current) {
          hasMarkedReadyRef.current = true;
          setIsReady(true);
        }
      },
    });

    return () => {
      globe.destroy();
      window.removeEventListener('resize', onResize);
    };
  }, [theme]);

  return (
    <div className="w-full max-w-[600px] aspect-square flex items-center justify-center opacity-100">
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          contain: "layout paint size",
          opacity: isReady ? 1 : 0,
          transition: "opacity 280ms ease-out",
        }}
      />
    </div>
  );
};

const Contact = () => {
  const [isFlipped, setIsFlipped] = useState(false);
  const { theme } = useContext(ThemeContext);

  const contactLinks = [
    {
      icon: Mail,
      label: "Email",
      value: "gulshan.sdet@gmail.com",
      href: "mailto:gulshan.sdet@gmail.com",
    },
    {
      icon: Linkedin,
      label: "LinkedIn",
      value: "Connect with me",
      href: "https://www.linkedin.com/in/gulshan98/",
    },
    {
      icon: Github,
      label: "GitHub",
      value: "Explore my code",
      href: "https://github.com/im-gulshan?tab=repositories",
    }
  ];

  const availabilityBadge = (
    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 dark:bg-gray-900/80 text-blue-600 dark:text-blue-400 font-medium text-sm border border-gray-200 dark:border-gray-800 backdrop-blur-md shadow-sm">
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500"></span>
      </span>
      Available for Opportunities
    </div>
  );

  return (
    <section id="contact" className="relative pt-8 pb-16 md:pt-16 md:pb-24 px-4 sm:px-6 md:px-16 bg-transparent overflow-hidden transition-colors duration-300">

      {/* Background Decoration */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[100px]"></div>
      </div>

      {/* Globe Container - Shifted right on desktop, center back on mobile */}
      <div className="absolute inset-0 pointer-events-none z-0 flex items-center justify-center md:justify-end md:-translate-x-12 lg:-translate-x-24 opacity-60 md:opacity-100 md:mix-blend-screen">
        <CobeGlobe theme={theme} />
      </div>

      <div className="max-w-7xl mx-auto w-full relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">

          {/* Left Text Segment */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center md:text-left drop-shadow-sm"
          >
            <div className="hidden md:inline-flex mb-6">
              {availabilityBadge}
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-5 md:mb-6 leading-[1.1] md:leading-tight tracking-tight">
              Let's Build <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
                Something Great
              </span>
            </h2>

            <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-md mx-auto md:mx-0 leading-relaxed mb-6 md:mb-8">
              I'm always excited to discuss new tech, collaborate on projects, or simply have a conversation about innovation.
            </p>

            <div className="flex items-center justify-center md:justify-start gap-3 text-gray-600 dark:text-gray-400 font-medium">
              <MapPin className="w-5 h-5 text-blue-500" />
              <span>Based in Noida, India</span>
            </div>

            <div className="md:hidden mt-4">
              {availabilityBadge}
            </div>

            {/* Mobile-only scroll indicator */}
            <motion.div
              className="md:hidden mt-8 flex justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", opacity: { duration: 1 } }}
            >
              <button
                onClick={() => document.getElementById('contact-card-container')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                className="group flex flex-col items-center text-gray-400 dark:text-gray-500 hover:text-blue-500 transition-colors focus:outline-none"
              >
                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] mb-3 opacity-70 group-hover:opacity-100 transition-opacity">Contact Details</span>
                <div className="w-10 h-10 rounded-full bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center group-hover:bg-white dark:group-hover:bg-gray-800 transition-all">
                  <ChevronDown className="w-5 h-5 text-blue-500" />
                </div>
              </button>
            </motion.div>
          </motion.div>

          {/* Right Frosted Glass Card / Flip Container */}
          <motion.div
            id="contact-card-container"
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="w-full max-w-md mx-auto md:mr-0 relative"
            style={{ perspective: '1000px' }}
          >

            <div
              className="relative w-full grid transition-transform duration-700"
              style={{ transformStyle: 'preserve-3d', transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
            >

              {/* Front Side: Links */}
              <div
                className={`w-full h-fit self-center flex flex-col backdrop-blur-2xl bg-white/70 dark:bg-gray-900/60 border border-white/50 dark:border-gray-700/50 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.3)] rounded-3xl p-6 sm:p-8 ${isFlipped ? 'pointer-events-none' : ''}`}
                style={{ gridArea: '1 / 1', backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
              >
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 select-none">Contact Details</h3>

                <div className="space-y-4">
                  {contactLinks.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.href}
                      target={link.href.startsWith('http') ? '_blank' : undefined}
                      rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="group flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl bg-white/50 dark:bg-gray-800/50 hover:bg-white dark:hover:bg-gray-800 border border-transparent hover:border-blue-100 dark:hover:border-gray-700 transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer"
                    >
                      <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <link.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider mb-0.5 sm:mb-1">{link.label}</p>
                        <p className="text-gray-900 dark:text-gray-100 font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors text-xs sm:text-base truncate">{link.value}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all duration-300 opacity-0 group-hover:opacity-100" />
                    </a>
                  ))}

                  {/* Telegram Flip Trigger */}
                  <button
                    onClick={() => setIsFlipped(true)}
                    className="w-full group flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl bg-gradient-to-r from-blue-600/10 to-purple-600/10 dark:from-blue-900/20 dark:to-purple-900/20 hover:from-blue-600/20 hover:to-purple-600/20 border border-blue-500/20 transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer text-left focus:outline-none"
                  >
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-blue-500 text-white rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-500/30">
                      <TelegramIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] sm:text-xs text-blue-600/80 dark:text-blue-400/80 font-semibold uppercase tracking-wider mb-0.5 sm:mb-1">Telegram</p>
                      <p className="text-blue-900 dark:text-blue-100 font-medium text-xs sm:text-base truncate">Community QR Code</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Back Side: Telegram QR Card Component */}
              <div
                className={`w-full h-full ${!isFlipped ? 'pointer-events-none' : ''}`}
                style={{ gridArea: '1 / 1', backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
              >
                <TelegramQRCard onFlipBack={() => setIsFlipped(false)} />
              </div>

            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
